// ASIS desktop — Modelos view. Per-role provider/model editor plus
// separate provider credential forms. Reuses the session's ApiClient and
// re-renders from a single GET /api/models. No calls fire on load: model
// credentials are entered explicitly and tested only on click. The
// search+openrouter combination is visibly unsupported (the test button
// is disabled with an explanation). Every request owns an AbortController;
// on destroy every controller is aborted, every password input cleared
// and no late renders happen.

import { ApiError, RequestAbortError } from '../../api/client.js';
import type { ModelPreference, ModelRole, ModelSettingsView, ModelProvider } from '../../api/connectors-types.js';
import { h, toast, confirmModal, type View } from '../components/dom.js';
import { renderHeader } from '../components/header.js';
import type { SessionController } from '../../session/controller.js';

const ROLES: Array<{ value: ModelRole; label: string; description: string }> = [
  { value: 'conversation', label: 'Conversación', description: 'Chat principal. La voz Gemini Live tiene una configuración independiente.' },
  { value: 'reasoning', label: 'Razonamiento', description: 'Pasa a otro modelo cuando el chat principal necesita pensar un poco más.' },
  { value: 'search', label: 'Búsqueda en la web', description: 'Modelo capaz de citar fuentes. La búsqueda web requiere Gemini en esta versión.' },
  { value: 'coding', label: 'Programación', description: 'Genera texto y código. ASIS nunca ejecuta nada de lo que devuelva.' },
];

const PROVIDERS: Array<{ value: ModelProvider; label: string }> = [
  { value: 'gemini', label: 'Gemini' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'omniroute', label: 'OmniRoute · DeepSeek' },
];

const DEFAULT_ROLES: Record<ModelRole, ModelPreference> = {
  conversation: { provider: 'gemini', model: 'gemini-3.1-flash-lite' },
  reasoning: { provider: 'gemini', model: 'gemini-3.1-flash-lite' },
  search: { provider: 'gemini', model: 'gemini-3.1-flash-lite' },
  coding: { provider: 'gemini', model: 'gemini-3.1-flash-lite' },
};

export function createModelosView(controller: SessionController): View {
  let alive = true;
  const lifetime = new AbortController();
  const root = h('section', { class: 'view view-modelos', id: 'view-modelos' });
  root.appendChild(renderHeader('modelos'));
  const body = h('div', { class: 'section-body' });
  root.appendChild(body);

  const api = controller.getApi();
  const passwordInputs = new Set<HTMLInputElement>();
  function trackSecret(input: HTMLInputElement): void { passwordInputs.add(input); }
  function clearSecrets(): void {
    for (const input of passwordInputs) {
      input.value = '';
      input.placeholder = '';
    }
    passwordInputs.clear();
  }

  const intro = h('section', { class: 'card' });
  intro.append(
    h('h2', { class: 'card-title' }, ['Modelos por función']),
    h('p', { class: 'card-sub' }, ['Elige qué modelo te ayuda en cada tarea. Los cambios sólo se aplican cuando guardas.']),
  );
  intro.classList.add('models-intro');
  body.appendChild(intro);

  const status = h('p', { class: 'card-sub' });
  intro.appendChild(status);

  const roleCard = h('section', { class: 'card role-card' });
  roleCard.append(h('h2', { class: 'card-title' }, ['Una función, un modelo']));
  const roleList = h('ul', { class: 'role-list' });
  roleCard.appendChild(roleList);
  body.appendChild(roleCard);

  const providerCard = h('section', { class: 'card provider-card' });
  providerCard.append(
    h('h2', { class: 'card-title' }, ['Credenciales de proveedor']),
    h('p', { class: 'card-sub' }, ['Configura la API key sólo del proveedor que uses. Gemini puede usar la clave del servidor o tu clave personal. Al quitar tu clave personal vuelve a la del servidor, si está disponible.']),
  );
  const advanced = h('details', { class: 'secondary-details models-advanced' }, [h('summary', {}, ['Proveedores y configuración de voz']), providerCard]);
  body.appendChild(advanced);

  const voiceCard = h('section', { class: 'card voice-card' });
  voiceCard.append(
    h('h2', { class: 'card-title' }, ['Voz']),
    h('p', { class: 'card-sub' }, ['La voz usa Gemini Live de forma separada a estos roles. No se ve afectada por los cambios de arriba.']),
  );
  advanced.appendChild(voiceCard);

  let settings: ModelSettingsView | null = null;
  let currentRoles: Record<ModelRole, ModelPreference> = cloneRoles(DEFAULT_ROLES);
  let dirtyRoles: Record<ModelRole, ModelPreference> = cloneRoles(currentRoles);
  const roleRows: Array<{ role: ModelRole; pending: boolean; note: HTMLElement; row: HTMLElement; provider: HTMLSelectElement; model: HTMLInputElement; test: HTMLButtonElement; result: HTMLElement; err: HTMLElement; abort: AbortController | null }> = [];

  let loadAbort: AbortController | null = null;
  let saveAbort: AbortController | null = null;
  const credAborts = new Map<ModelProvider, AbortController>();
  let saving = false;

  const providerForms: Record<Exclude<ModelProvider, 'omniroute'>, { form: HTMLFormElement; keyInput: HTMLInputElement; status: HTMLElement; err: HTMLElement; save: HTMLButtonElement; clear: HTMLButtonElement } > = {
    gemini: { form: h('form', { class: 'form-stack' }), keyInput: h('input', { class: 'input', type: 'password', autocomplete: 'off', spellcheck: 'false', 'aria-label': 'API key de Gemini' }), status: h('p', { class: 'card-sub' }), err: h('p', { class: 'auth-error', role: 'alert' }), save: h('button', { type: 'submit', class: 'btn primary' }, ['Guardar clave Gemini']), clear: h('button', { type: 'button', class: 'btn ghost' }, ['Quitar clave']) },
    openrouter: { form: h('form', { class: 'form-stack' }), keyInput: h('input', { class: 'input', type: 'password', autocomplete: 'off', spellcheck: 'false', 'aria-label': 'API key de OpenRouter' }), status: h('p', { class: 'card-sub' }), err: h('p', { class: 'auth-error', role: 'alert' }), save: h('button', { type: 'submit', class: 'btn primary' }, ['Guardar clave OpenRouter']), clear: h('button', { type: 'button', class: 'btn ghost' }, ['Quitar clave']) },
  };
  for (const p of ['gemini', 'openrouter'] as const) {
    const slot = providerForms[p];
    trackSecret(slot.keyInput);
    const keyLabel = h('label', { class: 'field' });
    keyLabel.append(h('span', {}, ['API key personal']), slot.keyInput);
    const actions = h('div', { class: 'form-row' });
    actions.append(slot.save, slot.clear);
    slot.form.append(keyLabel, actions, slot.status, slot.err);
    const block = h('section', { class: 'card subcard' });
    block.append(h('h3', { class: 'card-title' }, [p === 'gemini' ? 'Gemini' : 'OpenRouter']));
    block.appendChild(slot.form);
    providerCard.appendChild(block);
  }

  const omniStatus = h('p', { class: 'card-sub' });
  providerCard.append(h('h3', { class: 'card-title' }, ['OmniRoute']), omniStatus, h('p', { class: 'card-sub' }, ['La conexión se gestiona en el servidor. No necesitas copiar su clave al dispositivo.']));

  function providerSourceLabel(p: ModelProvider): string {
    if (!settings) return 'Cargando…';
    if (p === 'omniroute') { const o = settings.providers.omniroute; return !o?.configured ? 'Sin configurar' : o.source === 'server' ? 'Configurada por el servidor' : 'Configurada por ti'; }
    if (p === 'gemini') {
      const g = settings.providers.gemini;
      if (!g.configured) return 'Sin configurar';
      return g.source === 'server' ? 'Configurada por el servidor' : 'Configurada por ti';
    }
    const o = settings.providers.openrouter;
    if (!o.configured) return 'Sin configurar';
    return 'Configurada por ti';
  }

  function renderProviderStatus(): void {
    omniStatus.textContent = providerSourceLabel('omniroute');
    for (const p of ['gemini', 'openrouter'] as const) {
      const slot = providerForms[p];
      slot.status.textContent = providerSourceLabel(p);
      const pending = saving || credAborts.size > 0;
      slot.keyInput.disabled = !settings || pending;
      slot.keyInput.placeholder = '';
      slot.save.disabled = !settings || pending;
      slot.clear.disabled = !settings || pending || settings.providers[p].source !== 'personal';
    }
  }

  function renderRoleRows(): void {
    for (const entry of roleRows) entry.abort?.abort();
    roleRows.length = 0;
    roleList.replaceChildren();
    for (const r of ROLES) {
      const li = h('li', { class: 'role-row' });
      const head = h('div', { class: 'role-head' });
      head.append(
        h('h3', { class: 'card-title' }, [r.label]),
        h('p', { class: 'card-sub' }, [r.description]),
      );
      const form = h('form', { class: 'form-stack model-role-form' });
      const providerSel = h('select', { class: 'select', 'aria-label': `Proveedor para ${r.label}` });
      for (const opt of PROVIDERS) {
        const o = h('option', { value: opt.value }, [opt.label]);
        if (dirtyRoles[r.value].provider === opt.value) o.selected = true;
        providerSel.appendChild(o);
      }
      const modelInput = h('input', { class: 'input', type: 'text', placeholder: 'id del modelo', 'aria-label': `Modelo para ${r.label}`, value: dirtyRoles[r.value].model });
      const actions = h('div', { class: 'form-row' });
      const testBtn = h('button', { type: 'button', class: 'btn ghost' }, ['Probar']);
      actions.appendChild(testBtn);
      const unsupported = h('p', { class: 'card-sub unsupported' });
      const testErr = h('p', { class: 'auth-error', role: 'alert' });
      const result = h('div', { class: 'model-result', 'aria-live': 'polite' });
      result.textContent = 'Sin resultados todavía.';
      form.append(
        field('Proveedor', providerSel, true),
        field('Modelo', modelInput, true),
        actions,
        unsupported,
        testErr,
        result,
      );
      li.append(head, form);
      roleList.appendChild(li);
      const entry = { role: r.value, pending: false, note: unsupported, row: li, provider: providerSel, model: modelInput, test: testBtn, result, err: testErr, abort: null as AbortController | null };
      roleRows.push(entry);

      providerSel.addEventListener('change', () => {
        if (providerSel.value === 'omniroute') modelInput.value = 'cmd/deepseek/deepseek-v4.1-flash';
        dirtyRoles[r.value] = { provider: providerSel.value as ModelProvider, model: modelInput.value.trim() };
        entry.abort?.abort(); entry.pending = false;
        markDirty();
      });
      modelInput.addEventListener('input', () => {
        entry.abort?.abort(); entry.pending = false;
        dirtyRoles[r.value] = { provider: providerSel.value as ModelProvider, model: modelInput.value.trim() };
        markDirty();
      });

      testBtn.addEventListener('click', async () => {
        if (testBtn.disabled) return;
        if (r.value === 'search' && providerSel.value !== 'gemini') {
          testErr.textContent = 'La búsqueda con este proveedor no está soportada; usa Gemini.';
          return;
        }
        if (!modelInput.value.trim()) { testErr.textContent = 'Introduce el id del modelo antes de probar.'; return; }
        if (!alive || isDirty() || saving) return;
        testBtn.disabled = true;
        testErr.textContent = '';
        entry.abort?.abort();
        entry.abort = new AbortController();
        const request = entry.abort;
        entry.pending = true;
        try {
          const res = await api.testModelRole({ role: r.value }, request.signal, 25000);
          if (!alive || request.signal.aborted) return;
          const technical = h('details', { class: 'model-result-details' }, [h('summary', {}, ['Detalles técnicos']), h('pre', { class: 'result-pre' }, [JSON.stringify(res, null, 2)])]);
          result.replaceChildren(
            h('p', { class: 'model-result-copy' }, [res.text]),
            h('p', { class: 'card-sub' }, [`${res.provider} · ${res.model}`]),
            technical,
          );
          toast(`Prueba ${r.label}: ${res.ok ? 'OK' : 'con avisos'}`, res.ok ? 'ok' : 'error');
        } catch (err) {
          if (!alive || request.signal.aborted || err instanceof RequestAbortError) return;
          if (err instanceof ApiError) testErr.textContent = err.message;
          else testErr.textContent = (err as Error).message;
        } finally { if (entry.abort === request) { entry.pending = false; updateUnsupported(entry, r.value); } }
      });

      updateUnsupported(entry, r.value);
    }
  }

  function updateUnsupported(entry: typeof roleRows[number], role: ModelRole): void {
    const unsupported = role === 'search' && entry.provider.value !== 'gemini';
    entry.note.textContent = unsupported ? 'La búsqueda con este proveedor no está soportada; usa Gemini.' : isDirty() ? 'Guarda los roles antes de probar esta configuración.' : '';
    entry.test.disabled = unsupported || isDirty() || saving || entry.pending || credAborts.size > 0 || !settings;
    entry.test.title = unsupported ? 'No soportado' : '';
  }

  function markDirty(): void {
    saveBtn.disabled = !settings || saving || credAborts.size > 0 || !isDirty();
    for (const entry of roleRows) updateUnsupported(entry, entry.role);
  }

  const saveBtn = h('button', { type: 'button', class: 'btn primary save-roles' }, ['Guardar roles']);
  saveBtn.disabled = true;
  saveBtn.addEventListener('click', () => { void saveRoles(); });
  roleCard.appendChild(saveBtn);
  const rolesErr = h('p', { class: 'auth-error', role: 'alert' });
  roleCard.appendChild(rolesErr);

  function isDirty(): boolean {
    return (Object.keys(currentRoles) as ModelRole[]).some(r => currentRoles[r].provider !== dirtyRoles[r].provider || currentRoles[r].model !== dirtyRoles[r].model);
  }

  async function saveRoles(): Promise<void> {
    if (!alive || !settings || saving || credAborts.size > 0 || !isDirty()) return;
    if (Object.values(dirtyRoles).some(role => !role.model || role.model.length > 200)) {
      rolesErr.textContent = 'Cada rol necesita un identificador de modelo válido (máximo 200 caracteres).'; return;
    }
    saveAbort?.abort(); const request = new AbortController(); saveAbort = request;
    saving = true; rolesErr.textContent = ''; markDirty();
    for (const entry of roleRows) { entry.provider.disabled = true; entry.model.disabled = true; entry.abort?.abort(); }
    try {
      const next = await api.patchModelSettings({ roles: cloneRoles(dirtyRoles) }, request.signal);
      if (!alive || request.signal.aborted) return;
      settings = next; currentRoles = cloneRoles(next.roles); dirtyRoles = cloneRoles(currentRoles);
      renderProviderStatus(); renderRoleRows(); toast('Roles guardados', 'ok');
    } catch (err) {
      if (alive && !request.signal.aborted && !(err instanceof RequestAbortError)) rolesErr.textContent = apiMessage(err);
    } finally {
      if (saveAbort === request) {
        saving = false;
        for (const entry of roleRows) { entry.provider.disabled = false; entry.model.disabled = false; }
        markDirty();
      }
    }
  }

  for (const p of ['gemini', 'openrouter'] as const) {
    const slot = providerForms[p];
    slot.form.addEventListener('submit', async e => {
      e.preventDefault();
      if (slot.save.disabled) return;
      const value = slot.keyInput.value;
      if (!value) { slot.err.textContent = 'Introduce la clave antes de guardar.'; return; }
      if (value.length > 4096) { slot.err.textContent = 'La clave es demasiado larga (máx. 4096 caracteres).'; return; }
      slot.save.disabled = slot.clear.disabled = true;
      credAborts.get(p)?.abort();
      const request = new AbortController();
      credAborts.set(p, request);
      renderProviderStatus(); markDirty();
      for (const entry of roleRows) { entry.abort?.abort(); entry.pending = false; entry.provider.disabled = true; entry.model.disabled = true; entry.result.textContent = 'Sin resultados todavía.'; }
      try {
        const next = await api.patchModelSettings({ provider: p, apiKey: value }, request.signal);
        if (!alive || request.signal.aborted) return;
        slot.keyInput.value = '';
        slot.keyInput.dispatchEvent(new Event('blur'));
        settings = next;
        renderProviderStatus();
        toast(`Clave ${p} guardada`, 'ok');
        slot.err.textContent = '';
      } catch (err) {
        if (!alive || request.signal.aborted || err instanceof RequestAbortError) return;
        if (err instanceof ApiError) slot.err.textContent = err.message;
        else slot.err.textContent = (err as Error).message;
      } finally {
        slot.keyInput.value = '';
        if (credAborts.get(p) === request) credAborts.delete(p);
        if (alive) { renderProviderStatus(); markDirty(); for (const entry of roleRows) { entry.provider.disabled = false; entry.model.disabled = false; } }
      }
    });
    slot.clear.addEventListener('click', async () => {
      if (slot.clear.disabled) return;
      const confirmation = confirmModal({ title: 'Quitar clave personal', body: `Se eliminará tu clave de ${p === 'gemini' ? 'Gemini' : 'OpenRouter'}. No elimina la clave gestionada por el servidor.`, confirmLabel: 'Quitar clave', danger: true });
      const dialog = document.querySelector('.modal-overlay:last-child');
      const cancel = () => (dialog?.querySelector('button') as HTMLButtonElement | null)?.click();
      lifetime.signal.addEventListener('abort', cancel, { once: true });
      const ok = await confirmation;
      lifetime.signal.removeEventListener('abort', cancel);
      if (!ok || !alive) return;
      slot.save.disabled = slot.clear.disabled = true;
      credAborts.get(p)?.abort();
      const request = new AbortController();
      credAborts.set(p, request);
      renderProviderStatus(); markDirty();
      for (const entry of roleRows) { entry.abort?.abort(); entry.pending = false; entry.provider.disabled = true; entry.model.disabled = true; entry.result.textContent = 'Sin resultados todavía.'; }
      try {
        const next = await api.patchModelSettings({ provider: p, apiKey: '' }, request.signal);
        if (!alive || request.signal.aborted) return;
        slot.keyInput.value = '';
        settings = next;
        renderProviderStatus();
        toast(`Clave ${p} eliminada`, 'ok');
        slot.err.textContent = '';
      } catch (err) {
        if (!alive || request.signal.aborted || err instanceof RequestAbortError) return;
        if (err instanceof ApiError) slot.err.textContent = err.message;
        else slot.err.textContent = (err as Error).message;
      } finally {
        slot.keyInput.value = '';
        if (credAborts.get(p) === request) credAborts.delete(p);
        if (alive) { renderProviderStatus(); markDirty(); for (const entry of roleRows) { entry.provider.disabled = false; entry.model.disabled = false; } }
      }
    });
  }

  async function load(): Promise<void> {
    if (!alive || saving || credAborts.size > 0) return;
    status.textContent = 'Cargando modelos…';
    loadAbort?.abort();
    loadAbort = new AbortController();
    const request = loadAbort;
    try {
      const r = await api.getModelSettings(request.signal);
      if (!alive || request.signal.aborted) return;
      settings = r;
      currentRoles = cloneRoles(r.roles);
      dirtyRoles = cloneRoles(currentRoles);
      status.textContent = `Voz: Gemini Live (constante). Búsqueda web disponible con Gemini.`;
      renderProviderStatus();
      renderRoleRows();
      markDirty();
    } catch (err) {
      if (!alive || err instanceof RequestAbortError) return;
      status.textContent = `No se pudo cargar: ${apiMessage(err)}`;
    }
  }

  function destroy(): void {
    alive = false;
    lifetime.abort();
    loadAbort?.abort();
    saveAbort?.abort();
    for (const request of credAborts.values()) request.abort();
    for (const e of roleRows) e.abort?.abort();
    clearSecrets();
  }

  const retry = h('button', { type: 'button', class: 'btn ghost small' }, ['Actualizar modelos']);
  retry.addEventListener('click', () => { if (!saving) void load(); }); intro.append(retry);
  renderProviderStatus();
  void load();
  return { el: root, destroy() { destroy(); }, update() { void load(); } };
}

function cloneRoles(r: Record<ModelRole, ModelPreference>): Record<ModelRole, ModelPreference> {
  return {
    conversation: { ...r.conversation },
    reasoning: { ...r.reasoning },
    search: { ...r.search },
    coding: { ...r.coding },
  };
}

function field(label: string, control: HTMLElement, required: boolean): HTMLElement {
  const wrap = h('label', { class: 'field' });
  wrap.append(h('span', {}, [label + (required ? ' *' : '')]), control);
  return wrap;
}

function apiMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return (err as Error).message ?? 'error';
}
